/* eslint-disable */
import { useState, useCallback } from 'react';
import { apiCaller } from 'utils/axios';
import { API_ENDPOINTS } from 'hooks/api/apiEndpoints';
import Swal from 'sweetalert2';

export const useVenue = () => {
    const [venues, setVenues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        pageNumber: 0,
        pageSize: 1000,
        totalElements: 0,
        totalPages: 0,
        first: true,
        last: true,
        empty: true
    });

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const handleError = useCallback((error) => {
        const errorMessage = error.message || 'An unexpected error occurred';
        Swal.fire({
            title: 'Error',
            text: errorMessage,
            icon: 'error'
        });
        setError(errorMessage);
    }, []);

    const listVenues = useCallback(async (page = 0, size = 1000) => {
        try {
            setLoading(true);
            const response = await apiCaller.get(`${API_ENDPOINTS.VENUES}?page=${page}&size=${size}`);
            
            // Check if response and response.data exist
            if (!response || !response.data) {
                setVenues([]);
                setPagination({
                    pageNumber: 0,
                    pageSize: size,
                    totalElements: 0,
                    totalPages: 0,
                    first: true,
                    last: true,
                    empty: true
                });
                return null;
            }

            const data = response.data;
            const content = data.content || [];
            const paginationData = {
                pageNumber: data.number || 0,
                pageSize: data.size || size,
                totalElements: data.totalElements || 0,
                totalPages: data.totalPages || 0,
                first: data.first || true,
                last: data.last || true,
                empty: data.empty || true
            };

            setVenues(content);
            setPagination(paginationData);
            
            return data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [handleError]);

    const getVenueById = useCallback(async (id) => {
        try {
            setLoading(true);
            const response = await apiCaller.get(`${API_ENDPOINTS.VENUES}/${id}`);
            return response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [handleError]);

    const createVenue = useCallback(async (venueData) => {
        try {
            setLoading(true);
            const response = await apiCaller.post(API_ENDPOINTS.VENUES, venueData);
            setVenues(prev => [response.data, ...prev]);
            showSuccessMessage('Venue created successfully');
            return response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [handleError, showSuccessMessage]);

    const updateVenue = useCallback(async (id, venueData) => {
        try {
            setLoading(true);
            const response = await apiCaller.put(`${API_ENDPOINTS.VENUES}/${id}`, venueData);
            setVenues(prev => prev.map(venue => 
                venue.id === id ? response.data : venue
            ));
            showSuccessMessage('Venue updated successfully');
            return response.data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [handleError, showSuccessMessage]);

    const deleteVenue = useCallback(async (id) => {
        try {
            setLoading(true);
            await apiCaller.delete(`${API_ENDPOINTS.VENUES}/${id}`);
            setVenues(prev => prev.filter(venue => venue.id !== id));
            showSuccessMessage('Venue deleted successfully');
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [handleError, showSuccessMessage]);

    return {
        venues,
        loading,
        error,
        pagination,
        listVenues,
        getVenueById,
        createVenue,
        updateVenue,
        deleteVenue
    };
};

export default useVenue; 